// Main entry point for privacy policy page
import '../../styles/main.css';
import '../../styles/components.css';
import '../../styles/document-legal-pages.css';
import './privacy.css';
import { initSentry } from '../../shared/sentry-config.ts';
import * as Sentry from '@sentry/react';
import { trackPageView } from '../../shared/services/analyticsService.ts';
import { Header } from '../../components/landing/header.js';
import { redirectWithLanguage, getCurrentLanguage } from '../../shared/utils/urlUtils.ts';
import i18n from '../../shared/i18n/config.ts';

// Initialize Sentry before other code
initSentry('web-privacy', 'privacy');

// Error handling with Sentry
window.addEventListener('error', (e) => {
  console.error('Global error:', e.error);
  Sentry.captureException(e.error, {
    tags: {
      errorType: 'global',
    },
  });
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled promise rejection:', e.reason);
  Sentry.captureException(e.reason, {
    tags: {
      errorType: 'unhandledRejection',
    },
  });
});

/**
 * Get translation by key
 */
function t(key, options = {}) {
  return i18n.t(key, options);
}

/**
 * Update all text on page with data-i18n attributes
 */
function updatePageText() {
  // Update elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n');
    if (key) {
      const translation = t(key);
      if (translation !== key) {
        element.textContent = translation;
      }
    }
  });
  
  // Populate lists dynamically
  populateLists();
}

/**
 * Populate list elements from translation arrays
 */
function populateLists() {
  // Account Data items
  const accountDataItems = t('privacy.informationWeCollect.accountData.items', { returnObjects: true });
  if (Array.isArray(accountDataItems) && accountDataItems.length > 0) {
    const list = document.getElementById('accountDataItems');
    if (list) {
      list.innerHTML = accountDataItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Subscription Data items
  const subscriptionDataItems = t('privacy.informationWeCollect.subscriptionData.items', { returnObjects: true });
  if (Array.isArray(subscriptionDataItems) && subscriptionDataItems.length > 0) {
    const list = document.getElementById('subscriptionDataItems');
    if (list) {
      list.innerHTML = subscriptionDataItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Machine Registration items
  const machineRegistrationItems = t('privacy.informationWeCollect.machineRegistration.items', { returnObjects: true });
  if (Array.isArray(machineRegistrationItems) && machineRegistrationItems.length > 0) {
    const list = document.getElementById('machineRegistrationItems');
    if (list) {
      list.innerHTML = machineRegistrationItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // User Datasets items
  const userDatasetsItems = t('privacy.informationWeCollect.userDatasets.items', { returnObjects: true });
  if (Array.isArray(userDatasetsItems) && userDatasetsItems.length > 0) {
    const list = document.getElementById('userDatasetsItems');
    if (list) {
      list.innerHTML = userDatasetsItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Team Data items
  const teamDataItems = t('privacy.informationWeCollect.teamData.items', { returnObjects: true });
  if (Array.isArray(teamDataItems) && teamDataItems.length > 0) {
    const list = document.getElementById('teamDataItems');
    if (list) {
      list.innerHTML = teamDataItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Usage Data items
  const usageDataItems = t('privacy.informationWeCollect.usageData.items', { returnObjects: true });
  if (Array.isArray(usageDataItems) && usageDataItems.length > 0) {
    const list = document.getElementById('usageDataItems');
    if (list) {
      list.innerHTML = usageDataItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Technical Data items
  const technicalDataItems = t('privacy.informationWeCollect.technicalData.items', { returnObjects: true });
  if (Array.isArray(technicalDataItems) && technicalDataItems.length > 0) {
    const list = document.getElementById('technicalDataItems');
    if (list) {
      list.innerHTML = technicalDataItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Service Provision items
  const serviceProvisionItems = t('privacy.howWeUse.serviceProvision.items', { returnObjects: true });
  if (Array.isArray(serviceProvisionItems) && serviceProvisionItems.length > 0) {
    const list = document.getElementById('serviceProvisionItems');
    if (list) {
      list.innerHTML = serviceProvisionItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Communication items
  const communicationItems = t('privacy.howWeUse.communication.items', { returnObjects: true });
  if (Array.isArray(communicationItems) && communicationItems.length > 0) {
    const list = document.getElementById('communicationItems');
    if (list) {
      list.innerHTML = communicationItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Improvement items
  const improvementItems = t('privacy.howWeUse.improvement.items', { returnObjects: true });
  if (Array.isArray(improvementItems) && improvementItems.length > 0) {
    const list = document.getElementById('improvementItems');
    if (list) {
      list.innerHTML = improvementItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Legal items
  const legalItems = t('privacy.howWeUse.legal.items', { returnObjects: true });
  if (Array.isArray(legalItems) && legalItems.length > 0) {
    const list = document.getElementById('legalItems');
    if (list) {
      list.innerHTML = legalItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Contract items
  const contractItems = t('privacy.legalBasis.contract.items', { returnObjects: true });
  if (Array.isArray(contractItems) && contractItems.length > 0) {
    const list = document.getElementById('contractItems');
    if (list) {
      list.innerHTML = contractItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Legitimate Interests items
  const legitimateInterestsItems = t('privacy.legalBasis.legitimateInterests.items', { returnObjects: true });
  if (Array.isArray(legitimateInterestsItems) && legitimateInterestsItems.length > 0) {
    const list = document.getElementById('legitimateInterestsItems');
    if (list) {
      list.innerHTML = legitimateInterestsItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Consent items
  const consentItems = t('privacy.legalBasis.consent.items', { returnObjects: true });
  if (Array.isArray(consentItems) && consentItems.length > 0) {
    const list = document.getElementById('consentItems');
    if (list) {
      list.innerHTML = consentItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Legal Obligation items
  const legalObligationItems = t('privacy.legalBasis.legalObligation.items', { returnObjects: true });
  if (Array.isArray(legalObligationItems) && legalObligationItems.length > 0) {
    const list = document.getElementById('legalObligationItems');
    if (list) {
      list.innerHTML = legalObligationItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Storage items
  const storageItems = t('privacy.dataStorage.storage.items', { returnObjects: true });
  if (Array.isArray(storageItems) && storageItems.length > 0) {
    const list = document.getElementById('storageItems');
    if (list) {
      list.innerHTML = storageItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Security items
  const securityItems = t('privacy.dataStorage.security.items', { returnObjects: true });
  if (Array.isArray(securityItems) && securityItems.length > 0) {
    const list = document.getElementById('securityItems');
    if (list) {
      list.innerHTML = securityItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Retention items
  const retentionItems = t('privacy.dataStorage.retention.items', { returnObjects: true });
  if (Array.isArray(retentionItems) && retentionItems.length > 0) {
    const list = document.getElementById('retentionItems');
    if (list) {
      list.innerHTML = retentionItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Safeguards items
  const safeguardsItems = t('privacy.internationalTransfers.safeguards.items', { returnObjects: true });
  if (Array.isArray(safeguardsItems) && safeguardsItems.length > 0) {
    const list = document.getElementById('safeguardsItems');
    if (list) {
      list.innerHTML = safeguardsItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Services items
  const servicesItems = t('privacy.internationalTransfers.services.items', { returnObjects: true });
  if (Array.isArray(servicesItems) && servicesItems.length > 0) {
    const list = document.getElementById('servicesItems');
    if (list) {
      list.innerHTML = servicesItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Essential Cookies items
  const essentialCookiesItems = t('privacy.cookies.essential.items', { returnObjects: true });
  if (Array.isArray(essentialCookiesItems) && essentialCookiesItems.length > 0) {
    const list = document.getElementById('essentialCookiesItems');
    if (list) {
      list.innerHTML = essentialCookiesItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Analytics Cookies items
  const analyticsCookiesItems = t('privacy.cookies.analytics.items', { returnObjects: true });
  if (Array.isArray(analyticsCookiesItems) && analyticsCookiesItems.length > 0) {
    const list = document.getElementById('analyticsCookiesItems');
    if (list) {
      list.innerHTML = analyticsCookiesItems.map(item => `<li>${item}</li>`).join('');
    }
  }


  // Contact items
  const contactItems = t('privacy.exerciseRights.contact.items', { returnObjects: true });
  if (Array.isArray(contactItems) && contactItems.length > 0) {
    const list = document.getElementById('contactItems');
    if (list) {
      list.innerHTML = contactItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Changes items
  const changesItems = t('privacy.changes.items', { returnObjects: true });
  if (Array.isArray(changesItems) && changesItems.length > 0) {
    const list = document.getElementById('changesItems');
    if (list) {
      list.innerHTML = changesItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Handle email link in contact section
  const emailText = t('privacy.contact.email');
  const emailElement = document.querySelector('[data-i18n="privacy.contact.email"]');
  if (emailElement && emailText) {
    const emailMatch = emailText.match(/support@[\w.]+/);
    if (emailMatch) {
      const emailAddress = emailMatch[0];
      emailElement.innerHTML = emailText.replace(
        emailAddress,
        `<a href="mailto:${emailAddress}" class="privacy-inline-link">${emailAddress}</a>`,
      );
    } else {
      emailElement.textContent = emailText;
    }
  }

  // Handle website link in supervisory authority section
  const websiteText = t('privacy.contact.supervisoryAuthority.website');
  const websiteElement = document.querySelector('[data-i18n="privacy.contact.supervisoryAuthority.website"]');
  if (websiteElement && websiteText) {
    // Extract URL from text (format: "Website: https://naih.hu")
    const urlMatch = websiteText.match(/https?:\/\/[\w.]+/);
    if (urlMatch) {
      const url = urlMatch[0];
      websiteElement.innerHTML = websiteText.replace(url, `<a href="${url}" target="_blank" rel="noopener noreferrer" class="privacy-inline-link">${url}</a>`);
    } else {
      websiteElement.textContent = websiteText;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  // Get language from URL or detect
  const lang = getCurrentLanguage();
  
  // Store language in localStorage for consistency
  localStorage.setItem('gantty_language', lang);
  
  // Set HTML lang attribute
  document.documentElement.lang = lang;
  
  // Change i18next language
  await i18n.changeLanguage(lang);
  
  // Update page title
  document.title = t('privacy.title') + ' - Gantty';
  
  // Track page view
  trackPageView('Privacy Notice');
  
  // Initialize header
  const header = new Header();
  
  // Update page text with translations
  updatePageText();
  
  // Update header language display after i18n is ready (with small delay to ensure DOM is ready)
  setTimeout(() => {
    if (header && typeof header.populateLanguageDropdown === 'function') {
      header.populateLanguageDropdown();
    }
  }, 100);
  
  // Listen for language changes
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
    document.title = t('privacy.title') + ' - Gantty';
    updatePageText();
    // Update header language display when language changes
    if (header && typeof header.populateLanguageDropdown === 'function') {
      header.populateLanguageDropdown();
    }
  });
  
  // Redirect to login page on login button click
  document.getElementById('loginBtn')?.addEventListener('click', async () => {
    redirectWithLanguage('/login');
  });
});

